import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import { AppBar, Box, Breadcrumbs, Chip, Grid, IconButton, Toolbar, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { styled } from '@mui/material/styles';
import Typography from "@mui/material/Typography";
import React, { useEffect, useRef, useState } from "react";
import { MicrospecThemeModePicker, UserAvatar } from "../Components";
import LanguagesMenu from "../Components/LanguagesMenu";
import { APPBAR_HEIGHT, QUICK_ACCESS_APPBAR_HEIGHT } from "../Helpers/Constants";
import { isRTL } from "../Helpers/Utils";
import { QuickAccessItems } from "../Models/Common";
import { useMicrospecThemeLanguageContext } from "../Themes";
import { drawerWidth } from "./Dashboard";

interface DashboardAppbarData {
  application: any;
  dashboard: any;
  handleDrawerToggle: () => void;
  item: any;
  organizationContextAware: boolean;
  quickAccessItems?: QuickAccessItems;
}

interface DashboardAppbarProps {
  data: DashboardAppbarData;
}

const DashboardAppbar: React.FC<DashboardAppbarProps> = ({ data }) => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const theme = useTheme();
  const { dashboard, handleDrawerToggle, item, quickAccessItems } = data;
  const dashboardName = dashboard?.name;
  const localizedDashboardName = localizationProvider.translateByType(dashboardName);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const hasQuickAccess = quickAccessItems && quickAccessItems.length > 0;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth } = scrollContainerRef.current;
        setShowScrollButtons(scrollWidth > clientWidth);
      }
    };

    checkScroll();
    const resizeObserver = new ResizeObserver(checkScroll);
    const currentRef = scrollContainerRef.current;

    if (currentRef) {
      resizeObserver.observe(currentRef);
      currentRef.addEventListener('scroll', checkScroll);
    }

    return () => {
      resizeObserver.disconnect();
      if (currentRef) {
        currentRef.removeEventListener('scroll', checkScroll);
      }
    };
  }, [hasQuickAccess]);

  const scroll = (direction: 'start' | 'end') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      const scrollValue = isRTL() ?
        (direction === 'start' ? scrollAmount : -scrollAmount) :
        (direction === 'start' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollBy({
        left: scrollValue,
        behavior: 'smooth'
      });
    }
  };

  const StyledAppBar = styled(AppBar)(({ theme }) => ({
    background: theme.palette.mode === 'dark'
      ? 'rgba(18, 18, 18, 0.8)'
      : 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    borderBottom: `1px solid ${theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'}`,
    boxShadow: "0 0 0 rgba(0, 0, 0, 0)", // Removed shadow for cleaner look
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: theme.palette.mode === 'dark'
        ? 'rgba(18, 18, 18, 0.9)'
        : 'rgba(255, 255, 255, 0.95)',
    }
  }));

  return (
    <>
      <StyledAppBar
        sx={{
          height: APPBAR_HEIGHT,
          maxHeight: APPBAR_HEIGHT,
          width: { md: `calc(100% - ${drawerWidth}px)` }, // Changed from sm to md
          ml: { md: !isRTL() ? `${drawerWidth}px` : "0px" }, // Changed from sm to md
          mr: { md: isRTL() ? `${drawerWidth}px` : "0px" }, // Changed from sm to md
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        enableColorOnDark
      >
        <Toolbar
          sx={{
            display: "flex",
            height: "inherit",
            px: { xs: 2, sm: 3 }, // Better padding
            justifyContent: "space-between"
          }}
          disableGutters
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                display: { md: "none" }, // Changed from sm to md to match drawer breakpoint
                mr: 2,
                color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'primary.main',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <Grid
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1.5,
              mx: 1.5
            }}
          >
            <Breadcrumbs
              separator="›"
              aria-label="breadcrumb"
              sx={{
                '& .MuiBreadcrumbs-separator': {
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                  mx: 0.5,
                  fontSize: '1.25rem',
                }
              }}
            >
              {
                !isMobile &&
                localizedDashboardName &&
                <Typography
                  key="dashboard-name"
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'primary.main',
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  {localizedDashboardName}
                </Typography>
              }
              <Typography
                key="item-name"
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'primary.main',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                {localizationProvider.translateByType(item.name)}
              </Typography>
            </Breadcrumbs>
          </Grid>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mx: 2,
            // Ensure child icons have proper contrast
            '& .MuiIconButton-root': {
              color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'primary.main',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(0, 0, 0, 0.1)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease-in-out',
            },
            '& .MuiSvgIcon-root': {
              color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'primary.main',
            }
          }}>
            <LanguagesMenu size="medium" />
            <MicrospecThemeModePicker size="medium" />
            {/* <NotificationsPopover /> */}
            <UserAvatar size="medium" />
          </Box>
        </Toolbar>
      </StyledAppBar>
      {hasQuickAccess && (
        <StyledAppBar
          sx={{
            height: QUICK_ACCESS_APPBAR_HEIGHT,
            maxHeight: QUICK_ACCESS_APPBAR_HEIGHT,
            top: `calc(${APPBAR_HEIGHT})`,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: !isRTL() ? `${drawerWidth}px` : "0px" },
            mr: { md: isRTL() ? `${drawerWidth}px` : "0px" },
            zIndex: (theme) => theme.zIndex.drawer - 1,
          }}
          enableColorOnDark
        >
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', height: '100%' }}>
            {showScrollButtons && (
              <IconButton
                onClick={() => scroll('start')}
                sx={{
                  position: 'absolute',
                  [isRTL() ? 'right' : 'left']: 0,
                  zIndex: 2,
                  height: '100%',
                  borderRadius: 0,
                  px: 1,
                  background: `linear-gradient(to ${isRTL() ? 'left' : 'right'}, ${theme.palette.mode === 'dark'
                    ? 'rgba(18, 18, 18, 0.95)'
                    : 'rgba(255, 255, 255, 0.95)'} 60%, transparent)`,
                  '&:hover': {
                    background: `linear-gradient(to ${isRTL() ? 'left' : 'right'}, ${theme.palette.mode === 'dark'
                      ? 'rgba(18, 18, 18, 1)'
                      : 'rgba(255, 255, 255, 1)'} 60%, transparent)`,
                  },
                }}
              >
                {isRTL() ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            )}

            <Box
              ref={scrollContainerRef}
              key={"quick-access-box"}
              sx={{
                display: 'flex',
                flex: 1,
                px: showScrollButtons ? { xs: 4, sm: 5 } : { xs: 1.5, sm: 2 },
                py: { xs: 0.5, sm: 0.5 },
                overflowX: 'auto',
                overflowY: 'hidden',
                gap: 1.2,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {quickAccessItems!.map((quickItem, index) => (
                <Tooltip
                  key={`${quickItem.id}-${index}`}
                  title={localizationProvider.translateByType(quickItem.description || quickItem.name)}
                  arrow
                >
                  <Chip
                    component={"a"}
                    href={quickItem.actionLink}
                    icon={quickItem.icon as any}
                    label={localizationProvider.translateByType(quickItem.name)}
                    clickable
                    size="small"
                    sx={{
                      height: `calc(${QUICK_ACCESS_APPBAR_HEIGHT} * 0.75)`,
                      fontSize: '0.75rem',
                      color: theme.palette.mode === 'dark' ? '#f0f0f0' : '#1f1f1f',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                      '& .MuiChip-label': { px: 1, py: 0, whiteSpace: 'nowrap' },
                      '& .MuiChip-icon': { fontSize: '0.875rem', ml: 0.5, color: 'inherit' },
                      '&:hover': {
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  />
                </Tooltip>
              ))}
            </Box>

            {showScrollButtons && (
              <IconButton
                onClick={() => scroll('end')}
                sx={{
                  position: 'absolute',
                  [isRTL() ? 'left' : 'right']: 0,
                  zIndex: 2,
                  height: '100%',
                  borderRadius: 0,
                  px: 1,
                  background: `linear-gradient(to ${isRTL() ? 'right' : 'left'}, ${theme.palette.mode === 'dark'
                    ? 'rgba(18, 18, 18, 0.95)'
                    : 'rgba(255, 255, 255, 0.95)'} 60%, transparent)`,
                  '&:hover': {
                    background: `linear-gradient(to ${isRTL() ? 'right' : 'left'}, ${theme.palette.mode === 'dark'
                      ? 'rgba(18, 18, 18, 1)'
                      : 'rgba(255, 255, 255, 1)'} 60%, transparent)`,
                  },
                }}
              >
                {isRTL() ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </IconButton>
            )}
          </Box>
        </StyledAppBar>
      )}
    </>
  );
};

export default DashboardAppbar;
